import { Navigate, useLocation } from "react-router-dom";

import { useMe } from "@/api/auth";

/**
 * Wrap any tree of routes that require an authenticated session.
 *
 * Behaviour:
 *   * `me` query in flight → render a placeholder so we don't bounce
 *     to /login on every page load (would lose state).
 *   * `me` errored (401 / network) → redirect to /login, remembering
 *     where the user wanted to go via location.state.from.
 *   * `me` succeeded → render children via <Outlet />.
 */
import type { PropsWithChildren } from "react";

export function RequireAuth({ children }: PropsWithChildren) {
  const me = useMe();
  const location = useLocation();

  if (me.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center text-slate-400">
        Loading...
      </div>
    );
  }

  if (me.isError || !me.data) {
    return (
      <Navigate to="/login" state={{ from: location.pathname }} replace />
    );
  }

  return <>{children}</>;
}
